import { fabric } from 'fabric';
import bind from 'bind-decorator';

import FabricNode from './fabric_node';
import {
  polygonPositionHandler,
  getObjectSizeWithStroke,
  actionHandler,
  anchorWrapper
} from './polygon_controls';

const BORDER_COLOR = '#cdd3dc';
const COLORS = {
  active: '#f0a884',
  available_map_with_schema: '#bef4c3',
  available_map_wo_schema_or_in_admin: '#bef4c3',
  disabled: '#e6e6e6',
  other_map_with_schema: '#e6e6e6',
  other_map_wo_schema: '#e6e6e6'
};

export default class Box extends FabricNode {
  initialize({
    map,
    id,
    state = this.$node.data('state'),
    size = this.$node.data('size'),
    width = this.$node.data('width'),
    height = this.$node.data('height'),
    isDisabled = false,
    fabricData
  }) {
    super.initialize(map, fabricData);

    if (this.isEditable) {
      this.width = parseFloat(this.$node.find('[data-field=width]').val());
      this.height = parseFloat(this.$node.find('[data-field=height]').val());
    } else {
      this.width = parseFloat(width);
      this.height = parseFloat(height);
    }

    this.id = id;
    this.size = size;
    this.state = state;
    this.text = String(this.$node.data('text'));

    this.$tag = this.$('.tag').length ? this.$('.tag') : this.$node;

    this.isDisabledValue = isDisabled;
    this.isActiveValue = false;
    this.isPolyValue = false;
    this.isPolyEditModeValue = false;
    this.polyPoints = null;

    this.$tag
      .on('click', e => {
        if (this.isEditable) {
          e.preventDefault();
          this.map.select(this);
        }
      })
      .on('mouseover', () => this.isActive = true)
      .on('mouseout', () => this.isActive = false);

    if (this.isEditable) {
      this._bindInputs();
      this._bindPolyControls();
    }
  }

  get strokeFillOptions() {
    return {
      stroke: this.map.isSchemaImage ? '' : BORDER_COLOR,
      strokeWidth: this.map.isSchemaImage ? 0 : 1, // this.map.scale(0.1),
      fill: this._color(),
      opacity: this.map.isSchemaImage ? 0.5 : 0.75
    };
  }

  get editPolyFabricOptions() {
    return {
      cornerColor: 'rgba(0, 0, 255, 0.5)',
      cornerSize: 12,
      cornerStyle: 'circle'
    };
  }

  set selectable(value) { // eslint-disable-line accessor-pairs
    this.fabricNode.selectable = value;
  }

  get hoverCursor() {
    return this.isEditable ?
      'pointer' :
      (this.isAvailable ? 'pointer' : 'not-allowed');
  }

  get isPoly() {
    return this.isPolyValue;
  }

  set isPoly(value) {
    if (value) {
      this.polyPoints = this._defaultPolyPoints();
    }
    if (this.isPolyValue === value) { return; }

    this.isPolyValue = value;
    this.$('.poly-edit-mode').toggleClass('d-none', !this.isPoly);

    if (this.fabricNode) {
      this.canvas.remove(this.fabricNode);
      this._render();
      this.map.select(this, true);
    }
  }

  get isPolyEditMode() {
    return this.isPolyEditModeValue;
  }

  set isPolyEditMode(value) {
    if (this.isPolyEditModeValue === value) { return; }

    this.isPolyEditModeValue = value;
    this.$('.poly-edit-mode')
      .toggleClass('btn-primary', !this.isPolyEditMode)
      .toggleClass('btn-danger', this.isPolyEditMode);

    if (this.isPolyEditMode) {
      const lastControl = this.fabricNode.points.length - 1;

      this.fabricNode.set(this.editPolyFabricOptions);
      this.fabricNode.controls =
        this.fabricNode.points.reduce((memo, point, index) => {
          memo['p' + index] = new fabric.Control({
            positionHandler: polygonPositionHandler,
            actionHandler: anchorWrapper(index > 0 ? index - 1 : lastControl, actionHandler),
            actionName: 'modifyPolygon',
            pointIndex: index
          });
          return memo;
        }, {});
    } else {
      this.fabricNode.controls = fabric.Object.prototype.controls;
      this.fabricNode.set(this.defaultFabricOptions);
    }

    this.canvas.requestRenderAll();
    this.map.select(this, true);
  }

  // public merthods
  async select() {
    this.node.classList.add('active');

    this.fabricNode.bringToFront();

    const { default: scrollIfNotVisible } = await import(
      'scroll-into-view-if-needed'
    );

    scrollIfNotVisible(this.node, {
      scrollMode: 'if-needed',
      block: 'center',
      inline: 'center'
    });
  }

  deselect() {
    this.node.classList.remove('active');
  }

  render() {
    this._render();
    this._syncInputs();
  }

  get isAvailable() {
    return this.state === 'available';
  }

  get groupedBoxes() {
    // no boxes grouping for map with schema
    if (this.map.isSchemaImage) {
      return [this];
    }

    const currentData = this.data();

    return this.map.boxes
      .filter(box => {
        if (this.width !== box.width || this.height !== box.height) {
          return false;
        }
        const boxData = box.data();

        return currentData.left === boxData.left &&
          currentData.top === boxData.top &&
          currentData.angle === boxData.angle;
      });
  }

  get isActive() {
    return this.isActiveValue;
  }

  set isActive(value) {
    if (this.isActiveValue === value) { return; }
    if (value && this.isPolyEditMode) { return; }
    if (!this.groupedBoxes.some(box => box.isAvailable)) { return; }

    if (value) {
      this.map.onBoxMouseOver(this);
    } else {
      this.map.onBoxMouseOut(this);
    }

    this.isActiveValue = value;
    this._update();
    this.$tag.toggleClass('active', value);
  }

  get isDisabled() {
    return this.isDisabledValue;
  }

  set isDisabled(value) {
    if (this.isDisabled === value) { return; }

    this.isDisabledValue = value;
    this._update();
    this.$tag.toggleClass('disabled', value);
  }

  // private methods
  _render(data = this.data()) {
    const fabricOption = {
      ...this.defaultFabricOptions,
      ...this.strokeFillOptions,
      snapAngle: 5,
      hoverCursor: this.hoverCursor
    };

    this.fabricNode = this.isPoly ?
      this._renderPoly(fabricOption) :
      this._renderRectangle(fabricOption);
    this.fabricNode.box = this;

    if (this.isEditable) {
      this.fabricNode
        .on('moving', () => this._syncInputs())
        .on('scaling', () => this._syncInputs())
        .on('resizing', () => this._syncInputs())
        .on('skewing', () => this._syncInputs())
        .on('rotating', () => this._syncInputs());
    }

    this.fabricNode
      .on('mouseover', () => this.isActive = true)
      .on('mouseout', () => this.isActive = false)
      .on('mouseup', this._emitSelect);

    this._syncFabricNode(null, data);

    this.canvas.add(this.fabricNode);
  }

  _renderPoly(fabricOption) {
    return new fabric.Polygon(this.polyPoints, { ...fabricOption });
  }

  _renderRectangle(fabricOption) {
    return new fabric.Polygon(this._defaultPolyPoints(), fabricOption);
  }

  _update() {
    this.fabricNode.set('fill', this._color());

    this.fabricNode.bringToFront();
    this.canvas.requestRenderAll();
  }

  _color() {
    const { groupedBoxes } = this;

    if (groupedBoxes.some(box => box.isActive)) {
      return COLORS.active;
    }
    if (groupedBoxes.some(box => box.isAvailable)) {
      if (groupedBoxes.some(box => !box.isDisabled)) {
        return this.map.isSchemaImage && !this.map.isEditable ?
          COLORS.available_map_with_schema :
          COLORS.available_map_wo_schema_or_in_admin;
      } else {
        return COLORS.disabled;
      }
    }

    return this.map.isSchemaImage ?
      COLORS.other_map_with_schema :
      COLORS.other_map_wo_schema;
  }

  _parseData(data = this.initialFabricData || this.$node.data('fabric_data')) {
    this.isPoly = parseInt(data.isPoly) === 1;
    this.polyPoints = data.polyPoints ?
      JSON.parse(data.polyPoints).map(({ x, y }) => ({
        x: this.map.scale(x),
        y: this.map.scale(y)
      })) :
      null;

    return {
      ...super._parseData(data),
      isPoly: this.isPoly,
      polyPoints: this.polyPoints
    };
  }

  _syncInputs(data = this.data()) {
    this.$isPoly ||= this.$node.find('[data-field=isPoly]');
    this.$polyPoints ||= this.$node.find('[data-field=polyPoints]');

    this.$isPoly.prop('checked', data.isPoly);
    this.$polyPoints.val(JSON.stringify(
      this.fabricNode.points.map(({ x, y }) => ({
        x: this.map.descale(x),
        y: this.map.descale(y)
      }))
    ));

    super._syncInputs(data);
  }

  _onInputChange(currentTarget) {
    const field = $(currentTarget).data('field');

    if (field === 'isPoly') {
      this.isPoly = currentTarget.checked;
    } else {
      super._onInputChange(currentTarget);
    }
  }

  @bind
  _emitSelect() {
    if (this.map.isSelectable) {
      $(this.node).trigger('map:select');
    }

    if (this.isAvailable) {
      this.map.onBoxClick(this);
    }
  }

  _bindPolyControls() {
    this.$('.poly-edit-mode').on('click', () => (
      this.isPolyEditMode = !this.isPolyEditMode
    ));
  }

  _defaultPolyPoints() {
    return [
      { x: this.map.scale(0), y: this.map.scale(0) },
      { x: this.map.scale(this.width), y: this.map.scale(0) },
      { x: this.map.scale(this.width), y: this.map.scale(this.height) },
      { x: this.map.scale(0), y: this.map.scale(this.height) }
    ];
  }
}
